import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import parse from 'html-react-parser';
import { Icon } from '@ohif/ui';
import ApiServices from '../../../../../platform/app/src/shared/ApiServices/ApiServices';
import { AILoader } from '../../../../../platform/app/src/shared/custom-ui';
import { getQueryParam } from "../../../../../platform/app/src/utils/queryParams";
import { saveReport } from './utils/reportSaveUtils';
import { useScrollDetection } from './utils/scrollUtils';
import { ScrollArrow } from './components';
import AiReportCreate from './AIReportCreate';
import './reportStyles.css';

interface TemplateSelectionProps {
    onTemplateSave: (data: { comments: string; template: string }) => void;
    template?: string;
    templateUrl?: string;
    templateContent?: string;
    setIsTemplateSelected?: (val: boolean) => void;
    setIsTemplateLoaded?: (val: boolean) => void;
    onCreatingReport?: (val: boolean) => void;
    onActiveTabChange?: (tab: 'classic' | 'radai' | 'dictation') => void;
}

function preprocessMedicalTemplateHtml(html: string) {
    if (!html) return html;

    return html.replace(/<\s*\/?\s*T[0-9][^>]*>/gi, match =>
        match.replace(/</g, '&lt;').replace(/>/g, '&gt;')
    );
}

const TemplateSelection = forwardRef(({ onTemplateSave, setIsTemplateSelected, setIsTemplateLoaded, onCreatingReport, onActiveTabChange }: TemplateSelectionProps, ref) => {
    const [activeTab, setActiveTab] = useState<'classic' | 'radai' | 'dictation'>('radai');
    const [indicatorStyle, setIndicatorStyle] = useState({});
    const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({ radai: null, classic: null, dictation: null });
    const dictationRef = useRef<{ handleSaveReport: () => Promise<void> } | null>(null);

    const [categories, setCategories] = useState<string[]>([]);
    const [templates, setTemplates] = useState<{ [key: string]: { key_name: string; presigned_url: string }[] }>({});
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedTemplateContent, setSelectedTemplateContent] = useState('');
    const [loadedTemplateContent, setLoadedTemplateContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [isCreatingReport, setIsCreatingReport] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const studyId = getQueryParam('studyId');

    const { showScrollArrow, scrollDown } = useScrollDetection(scrollRef, selectedTemplateContent);

    useEffect(() => {
        onCreatingReport?.(isCreatingReport);
    }, [isCreatingReport]);

    const { getValues, reset } = useForm({
        defaultValues: { reportComments: '' },
    });

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            if (activeTab === 'classic') {
                const data = await ApiServices.getAllReportTemplates();
                setCategories(Object.keys(data));
                setTemplates(data);
            } else if (activeTab === 'radai') {
                const data = await ApiServices.retrieveTemplateReport({ studyId });
                if (data?.presigned_url) {
                    setIsTemplateSelected?.(true);
                    setIsTemplateLoaded?.(false);
                    const html = await fetch(data.presigned_url).then(r => r.text());
                    const safeHtml = preprocessMedicalTemplateHtml(html);

                    setSelectedTemplateContent(safeHtml);
                    setLoadedTemplateContent(safeHtml);

                    setIsTemplateLoaded?.(true);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateSelect = async (templateKey: string) => {
        const selected = templates[selectedCategory]?.find(t => t.key_name === templateKey);
        if (!selected) return;

        try {
            window.dispatchEvent(new CustomEvent('stopVoiceDictation', {
                detail: { reason: 'template_changing' }
            }));

            setIsTemplateSelected?.(true);
            setIsTemplateLoaded?.(false);

            const html = await fetch(selected.presigned_url).then(r => r.text());
            const safeHtml = preprocessMedicalTemplateHtml(html);

            setSelectedTemplateContent(safeHtml);
            setLoadedTemplateContent(safeHtml);

            setIsTemplateLoaded?.(true);
        } catch (err) {
            toast.error('Failed to load template content');
        }
    };

    const handleRadAiSearch = async () => {
        if (!searchQuery?.trim()) {
            toast.warning('Please enter a search term.');
            return;
        }

        window.dispatchEvent(new CustomEvent('stopVoiceDictation', {
            detail: { reason: 'radai_generating' }
        }));

        setIsCreatingReport(true);
        try {
            const data = await ApiServices.retrieveTemplateReport({ studyId, search: searchQuery });

            if (data?.presigned_url) {
                setIsTemplateSelected?.(true);
                setIsTemplateLoaded?.(false);

                const html = await fetch(data.presigned_url).then(r => r.text());
                const safeHtml = preprocessMedicalTemplateHtml(html);

                setSelectedTemplateContent(safeHtml);
                setLoadedTemplateContent(safeHtml);

                setIsTemplateLoaded?.(true);
            } else {
                toast.warning('No RadAI report found for this query.');
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message);
        } finally {
            setIsCreatingReport(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
        reset();
        setSelectedTemplate('');
        setSelectedCategory('');
        setSelectedTemplateContent('');
        setLoadedTemplateContent('');
        setIsTemplateSelected?.(false);
        setIsTemplateLoaded?.(false);
    }, [activeTab]);

    const updateIndicator = () => {
        const activeBtn = buttonRefs.current[activeTab];
        if (activeBtn) {
            const { offsetWidth: buttonWidth, offsetLeft, scrollWidth: contentWidth } = activeBtn;

            const contentLeft = offsetLeft + (buttonWidth - contentWidth) / 2;
            const underlineWidth = contentWidth * 0.8;
            const centerLeft = contentLeft + (contentWidth - underlineWidth) / 2;

            setIndicatorStyle({
                width: `${underlineWidth}px`,
                left: `${centerLeft}px`,
            });
        }
    };

    useEffect(() => {
        updateIndicator();
        window.addEventListener('resize', updateIndicator);
        return () => {
            window.removeEventListener('resize', updateIndicator);
        };
    }, [activeTab]);

    useImperativeHandle(ref, () => ({
        async handleSaveReport() {
            if (activeTab === 'dictation') {
                if (dictationRef.current?.handleSaveReport) {
                    await dictationRef.current.handleSaveReport();
                    onTemplateSave({
                        comments: '',
                        template: 'Dictation report saved via AIReportCreate'
                    });
                } else {
                    toast.error('Dictation report not ready for saving.');
                }
                return;
            }

            const reportComments = getValues('reportComments');
            const container = scrollRef.current;
            if (!container) {
                toast.error('Template not loaded.');
                return;
            }

            window.dispatchEvent(new CustomEvent('stopVoiceDictation', {
                detail: { reason: 'saving_template' }
            }));

            setLoading(true);
            try {
                await saveReport(container, {
                    studyId,
                    successMessage: 'Report uploaded successfully!',
                    errorMessage: 'Error uploading report'
                });

                onTemplateSave({
                    comments: reportComments,
                    template: container.innerHTML
                });
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        },
        setActiveSubTab(tab: 'classic' | 'radai') {
            setActiveTab(tab);
        }
    }));

    const handleTabChange = (tab: 'classic' | 'radai' | 'dictation') => {
        setActiveTab(tab);
        onActiveTabChange?.(tab);

        if (tab === 'dictation') {
            // Dictation mode: Save is handled inside AiReportCreate + parent hides footer Save button
            // No template selection/loading flags needed here
            setIsTemplateSelected?.(false);
            setIsTemplateLoaded?.(false);
        } else {
            // Reset for template modes
            setIsTemplateSelected?.(false);
            setIsTemplateLoaded?.(false);
        }
    };

    return (
        <div className="flex h-full flex-col relative">

            <div className="relative flex flex-col items-center justify-center mb-4">
                <div className="relative flex items-center justify-center gap-6">
                    {[
                        { tab: 'radai', label: 'Rad AI', icon: 'sparkles-Icon' },
                        { tab: 'classic', label: 'Classic', icon: 'open-book' },
                        { tab: 'dictation', label: 'Dictation', icon: 'MicroPhone-icon' },
                    ].map(({ tab, label, icon }) => (
                        <div key={tab} className="relative">
                            <button
                                key={tab}
                                ref={el => { buttonRefs.current[tab] = el; }}
                                onClick={() => {
                                    handleTabChange(tab as 'classic' | 'radai' | 'dictation');
                                    setSearchQuery('');
                                    setSelectedTemplate('');
                                    setSelectedCategory('');
                                    setSelectedTemplateContent('');
                                }}
                                className={`relative overflow-hidden text-md flex items-center gap-2 rounded-full px-6 py-2 font-semibold capitalize transition-all duration-300
                                    ${activeTab === tab
                                        ? 'shadow-md'
                                        : 'backdrop-blur-sm shadow-inner hover:scale-105'}`}
                                style={{
                                    backgroundColor: activeTab === tab ? 'var(--bg-card)' : 'var(--bg-hover)',
                                    color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-secondary)',
                                    border: `1px solid ${activeTab === tab ? 'var(--border-subtle)' : 'transparent'}`,
                                }}
                            >
                                <Icon name={icon} className="h-5 w-5" />
                                {label}
                            </button>

                            {tab === 'dictation' && (
                                <span
                                    className="absolute -top-2 -right-2
                                        text-white text-[10px] font-bold uppercase tracking-wide px-2 py-[2px] rounded-full
                                        shadow-sm border border-white/20 select-none"
                                    style={{ background: '#045eb8' }}
                                >
                                    Beta
                                </span>
                            )}
                        </div>
                    ))}

                    <div
                        className="absolute bottom-0 h-1 rounded-full transition-all duration-300"
                        style={{
                            ...indicatorStyle,
                            position: 'absolute',
                            height: '3px',
                            borderRadius: '9999px',
                            transition: 'left 0.3s ease, width 0.3s ease',
                            background: '#045eb8',
                        }}
                    />
                </div>
            </div>

            <div
                className="flex-1 rounded-b-xl px-4 pt-4 shadow-inner custom-scrollbar"
                style={{ backgroundColor: 'var(--bg-card)' }}
            >
                {activeTab === 'classic' && (
                    <div
                        className="flex flex-col gap-4 md:flex-row p-4 rounded-lg shadow-md"
                        style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
                    >
                        <select
                            className="w-full rounded-lg p-2 shadow-sm"
                            style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}
                            value={selectedCategory}
                            onChange={(e) => {
                                setSelectedCategory(e.target.value);
                                setSelectedTemplate('');
                                setSelectedTemplateContent('');
                                setIsTemplateSelected?.(false);
                                setIsTemplateLoaded?.(false);
                            }}
                        >
                            <option value="">Select a Category</option>
                            {categories.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                        <select
                            className="w-full rounded-lg p-2 shadow-sm"
                            style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}
                            value={selectedTemplate}
                            onChange={(e) => {
                                const key = e.target.value;
                                setSelectedTemplate(key);
                                handleTemplateSelect(key);
                            }}
                            disabled={!selectedCategory}
                        >
                            <option value="">Select a template</option>
                            {(templates[selectedCategory] || []).map((t) => (
                                <option key={t.key_name} value={t.key_name}>
                                    {t.key_name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {!loading && activeTab === 'radai' && (
                    <div
                        className="flex flex-col gap-4 p-4 rounded-lg shadow-md"
                        style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
                    >
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            Enter clinical keywords to generate a more relevant RadAI report.
                        </p>
                        <div className="flex items-center gap-4">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="e.g., chest pain, CT thorax, post-op review"
                                className="flex-1 rounded-lg px-4 py-2 shadow-sm"
                                style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}
                            />
                            <button
                                onClick={handleRadAiSearch}
                                disabled={isCreatingReport || !searchQuery.trim()}
                                className={`flex items-center justify-center gap-2 rounded-lg px-5 py-2 shadow-md transition hover:shadow-lg ${isCreatingReport || !searchQuery.trim()
                                    ? 'cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                                style={isCreatingReport || !searchQuery.trim() ? {
                                    backgroundColor: 'var(--bg-hover)',
                                    color: 'var(--text-muted)',
                                } : undefined}
                            >
                                <Icon name="sparkles-Icon" className="h-5 w-5" />
                                {isCreatingReport ? 'Generating...' : 'Generate'}
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'dictation' && (
                    <AiReportCreate ref={dictationRef} />
                )}

                {selectedTemplateContent && activeTab !== 'dictation' && (
                    <div className="relative mt-6 flex-1">
                        <div
                            ref={scrollRef}
                            className="report-html-content overflow-y-auto custom-scrollbar"
                            id="report"
                            style={{
                                maxHeight:
                                    activeTab === 'classic'
                                        ? 'calc(100vh - 361px)'
                                        : activeTab === 'radai'
                                            ? 'calc(100vh - 397px)'
                                            : 'none',
                            }}
                        >
                            {parse(selectedTemplateContent)}
                        </div>
                        <ScrollArrow visible={showScrollArrow} onScroll={scrollDown} />
                    </div>
                )}
            </div>
            {loading && !selectedTemplateContent && (
                <div className="absolute inset-0 z-50 flex items-center justify-center">
                    <div className="flex flex-col items-center space-y-4">
                        <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
                    </div>
                </div>
            )}
            {isCreatingReport && (
                <AILoader />
            )}
        </div>
    );
});

export default TemplateSelection;
